import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { Modal, Button, ToggleContainer, Section, SelectHierarchyLabel, Date, TooltipHelp } from '@weezevent/nacre';
import moment from 'moment-timezone';
import i18n from 'i18next';
import Style from './exportModal.module.css';

export default function ExportModal({ openModal, organization, onAccept, onClose, modalTitle, modalBody, events, enableEventsFilter }) {
    const { t } = useTranslation();
    const locale = i18n.language;

    modalTitle = modalTitle || t('ticket.export.export_title');
    modalBody = modalBody || t('ticket.export.export_description');

    const [startDate, setStart] = React.useState(null);
    const [endDate, setEnd] = React.useState(null);
    const [errors, setErrors] = React.useState([false, false]);
    const [selected, setSelected] = React.useState([]);

    const setDate = React.useCallback(
        (name, value) => {
            //Generate a moment object with the value
            let newDate = moment.tz(value, 'DD/MM/YYYY', organization.timezone);
            //If we handle the start date
            if (name === 'start_date') {
                //If the value is not null (Handle empty date / or clear) but the date is not valid
                if (value && !newDate.isValid()) {
                    setErrors(['ticket.export.invalid_date', errors[1]]);
                } else if (endDate) {
                    //If we have a end date
                    if (newDate.isAfter(endDate)) {
                        //check if end date is not anterior
                        setErrors(['ticket.export.dateend_before_error', errors[1]]);
                    } else {
                        //If everything ok => reset errors
                        setErrors([false, false]);
                    }
                } //If the value is null (empty date after click on clear for example we set null to avoid invalid date)
                return !value ? setStart(null) : setStart(newDate);
            } else {
                if (value && !newDate.isValid()) {
                    setErrors([errors[0], 'ticket.export.invalid_date']);
                } else if (startDate) {
                    if (startDate.isAfter(newDate)) {
                        setErrors([errors[0], 'ticket.export.dateend_before_error']);
                    } else {
                        setErrors([false, false]);
                    }
                }
                return !value ? setEnd(null) : setEnd(newDate);
            }
        },
        [startDate, endDate]
    );

    const handleChange = React.useCallback(
        selected => {
            let selectedTmp = !Array.isArray(selected) ? [selected] : selected;
            setSelected(selectedTmp);
        },
        [setSelected]
    );

    const handleLabelChange = selected => {
        return selected.length ? t('ticket.export.modal.selected', { count: selected.length }) : '';
    };

    const options = React.useMemo(() => {
        return (
            events?.length &&
            events.map(event => {
                return { text: event.name, value: event.id };
            })
        );
    }, [events]);

    const onCloseModal = React.useCallback(() => {
        onClose();
        setStart(null);
        setEnd(null);
        setErrors([false, false]);
    }, [onClose]);

    const _onAccept = React.useCallback(() => {
        onAccept({ startDate, endDate, selected });
        setStart(null);
        setEnd(null);
        setSelected([]);
    }, [onAccept, startDate, endDate, selected]);

    return (
        <Modal
            allowExternalClosing={false}
            title={modalTitle}
            subtitle={modalBody}
            submitButton={<Button primary disabled={errors[0] || errors[1]} onClick={_onAccept} label={t('ticket.common.action.validate')} />}
            cancelButton={<Button onClick={onCloseModal} label={t('ticket.common.action.cancel')} />}
            open={Boolean(openModal)}
            onClose={onCloseModal}
        >
            <div>
                <ToggleContainer inlinePosition="left" title={t('ticket.export.modal.date-toggle')}>
                    <Section>
                        <div className={Style['export-button-dates']}>
                            <div className={Style['export-button-start-date']}>
                                <label>{t('ticket.common.date.start')}</label>
                                <Date.Input
                                    value={startDate || ''}
                                    locale={locale}
                                    onChange={value => {
                                        setDate('start_date', value);
                                    }}
                                    minYear={2010}
                                    maxYear={2050}
                                    returnFormat={'DD/MM/YYYY'}
                                />
                            </div>
                            <div className={Style['export-button-end-date']}>
                                <label>{t('ticket.common.date.end')}</label>
                                <Date.Input
                                    value={endDate || ''}
                                    locale={locale}
                                    onChange={value => {
                                        setDate('end_date', value);
                                    }}
                                    minYear={2010}
                                    maxYear={2050}
                                    returnFormat={'DD/MM/YYYY'}
                                />
                            </div>
                        </div>
                        <div className={Style['export-button-dates-error']}>
                            {Boolean(errors[0]) && <label>{t(errors[0])}</label>}
                            {Boolean(errors[1]) && <label>{t(errors[1])}</label>}
                        </div>
                    </Section>
                </ToggleContainer>
            </div>
            {events && (
                <div>
                    <ToggleContainer
                        disabled={!enableEventsFilter}
                        inlinePosition="left"
                        title={
                            <span>
                                {t('ticket.export.modal.event-toggle')}
                                {!enableEventsFilter && <TooltipHelp title={t('ticket.export.modal.available-soon')} />}
                            </span>
                        }
                    >
                        <Section>
                            <SelectHierarchyLabel
                                multi
                                options={options}
                                selected={selected}
                                onChange={handleChange}
                                onLabelSelected={handleLabelChange}
                                totalItems={events.length}
                                label={t('ticket.export.modal.include-events')}
                                placeholder={t('ticket.charts.chart.events_selected')}
                                placeholderSearch={t('ticket.common.action.search')}
                                labelSelectAll={t('ticket.export.modal.select-all')}
                                labelApply={t('ticket.common.action.select')}
                            />
                        </Section>
                    </ToggleContainer>
                </div>
            )}
        </Modal>
    );
}

ExportModal.propTypes = {
    openModal: PropTypes.bool.isRequired,
    onAccept: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    organization: PropTypes.object.isRequired,
    events: PropTypes.object,
    modalBody: PropTypes.string,
    modalTitle: PropTypes.string
};
